import type { Client, QuoteRecord } from '../types';
import { fetchQuotes, fetchServerClients } from './api';
import { mergeQuotes } from './mergeQuotes';
import { loadClients, loadQuotes, saveClients, saveQuote } from './skyluxStore';

function asClient(row: unknown): Client | null {
  if (!row || typeof row !== 'object') return null;
  const c = row as Record<string, unknown>;
  const id = typeof c.id === 'string' ? c.id : '';
  if (!id) return null;
  return {
    id,
    name: typeof c.name === 'string' ? c.name : 'Traveler',
    email: typeof c.email === 'string' ? c.email : '',
    phone: typeof c.phone === 'string' ? c.phone : undefined,
    notes: typeof c.notes === 'string' ? c.notes : undefined,
    requestDate: typeof c.requestDate === 'string' ? c.requestDate : new Date().toISOString().slice(0, 10),
    status:
      c.status === 'pending' || c.status === 'quoted' || c.status === 'paid' ? c.status : 'quoted',
    origin: typeof c.origin === 'string' ? c.origin : undefined,
    destination: typeof c.destination === 'string' ? c.destination : undefined,
    travelDate: typeof c.travelDate === 'string' ? c.travelDate : undefined,
    source: c.source === 'landing' || c.source === 'manual' ? c.source : undefined,
  };
}

function mergeClients(apiRows: Client[], localRows: Client[]): Client[] {
  const byId = new Map<string, Client>();
  for (const c of localRows) byId.set(c.id, c);
  for (const c of apiRows) {
    const prev = byId.get(c.id);
    byId.set(c.id, prev ? { ...prev, ...c, notes: prev.notes ?? c.notes } : c);
  }
  return Array.from(byId.values());
}

/** Pull quotes + CRM clients from API into browser storage (for admin views). */
export async function syncAdminFromServer(seedClients: Client[]): Promise<void> {
  const [fromApiQuotes, fromApiClients] = await Promise.all([
    fetchQuotes().catch(() => [] as QuoteRecord[]),
    fetchServerClients().catch(() => [] as unknown[]),
  ]);

  const mergedQuotes = mergeQuotes(fromApiQuotes, loadQuotes());
  for (const q of mergedQuotes) saveQuote(q);

  const apiClients = fromApiClients.map(asClient).filter((c): c is Client => c != null);
  const mergedClients = mergeClients(apiClients, loadClients(seedClients));
  saveClients(mergedClients);
}
